(ns hai.home.desktop.newtab-toggle
  "The \"Open Humble on new tabs\" control shown in the top-right of Home.

   Home is what the browser extension frames as the new-tab page, so this is
   the surface where the override is most discoverable — the setting is
   otherwise buried in the profile page. It reads and writes exactly the same
   state as that settings row (`:user-config.newtab/override?`, via
   `auth/set-newtab-override`), so flipping either updates both.

   A hand-rolled `role=\"switch\"` button rather than `c/Switch`: the Radix
   Themes switch takes its colour from the theme accent, and this control needs
   the muted Chalk/Ink treatment plus its own focus ring. Same approach as the
   mini-app card toggle in [[hai.mini-apps.card]]."
  (:require
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.auth :as auth]
   [lambdaisland.ornament :as o]))

(def ^:private label-id "home-newtab-toggle-label")

;; Right-aligned and above the clock rather than absolutely positioned: the
;; top-right of Home only looks empty on wide viewports — below ~900px the
;; clock and notes panels grow into that space and would collide with it.
(o/defstyled toolbar :div
  {:display         "flex"
   :justify-content "flex-end"
   :align-items     "center"
   :margin-bottom   "16px"}
  [:at-media {:max-width "900px"}
   {:justify-content "flex-start"}])

(o/defstyled pill :div
  {:display       "inline-flex"
   :align-items   "center"
   :gap           "10px"
   :padding       "7px 14px"
   :border-radius "999px"
   :background    t/--colour-chalk}
  [:.label
   {:font-size   "13px"
    :font-weight "500"
    :line-height "1.2"
    :color       t/--colour-dim
    :user-select "none"}])

;; Off state carries its boundary in the border rather than a fill, so the
;; control stays quiet while still clearing the 3:1 non-text contrast bar
;; against the Chalk pill (Dim on Chalk is 5.98:1). A pale grey track — Rock,
;; say — would have come in at ~1.4:1.
(o/defstyled switch :button
  {:position      "relative"
   :flex-shrink   "0"
   :box-sizing    "border-box"
   :width         "40px"
   :height        "24px"
   :padding       "0"
   :border-width  "2px"
   :border-style  "solid"
   :border-color  t/--colour-dim
   :border-radius "999px"
   :background    t/--colour-white
   :cursor        "pointer"
   :transition    "background .18s ease, border-color .18s ease"}
  [:&.on {:background t/--colour-ink :border-color t/--colour-ink}]
  [:&:focus-visible
   {:outline-width  "2px"
    :outline-style  "solid"
    :outline-color  t/--humble-primary
    :outline-offset "2px"}]
  ;; Expand the pointer target to 44x44 without disturbing the pill's layout.
  [:&::before
   {:content   "''"
    :position  "absolute"
    :top       "50%"
    :left      "50%"
    :width     "44px"
    :height    "44px"
    :transform "translate(-50%, -50%)"}]
  [:.knob
   {:position      "absolute"
    :top           "2px"
    :left          "2px"
    :width         "16px"
    :height        "16px"
    :border-radius "50%"
    :background    t/--colour-dim
    :transition    "transform .18s ease, background .18s ease"}]
  [:&.on [:.knob {:background t/--colour-white :transform "translateX(16px)"}]]
  [:at-media {:prefers-reduced-motion "reduce"}
   {:transition "none"}
   [:.knob {:transition "none"}]])

(defn control
  "Toggle for the new-tab override, for the top-right of the Home page."
  []
  (let [on? (boolean (:user-config.newtab/override? @(<s :auth/identity)))]
    [toolbar
     [pill
      [:span.label {:id label-id} auth/newtab-toggle-label]
      [switch
       {:type            "button"
        :role            "switch"
        :aria-checked    (str on?)
        ;; Named by the visible label rather than a separate `aria-label`, so
        ;; the accessible name and the on-screen text can't diverge.
        :aria-labelledby label-id
        :class           (when on? "on")
        :on-click        #(>e [::auth/set-newtab-override (not on?)])}
       [:span.knob]]]]))
